// ---------------------------------------------------------------------------
// Link preview cards — Spencer (Short Form team): "if I post a link to an ig
// reel… we don't have to be brought off-app." Given a rendered task/comment
// HTML body, find up to 3 unique external links, ask the server what's behind
// each (GET /link-preview — scraped + cached server-side), and render compact
// cards under the content. YouTube links go one further: click plays the
// video IN the app via the privacy-enhanced youtube-nocookie embed.
//
// The failure mode is silence, on purpose: nothing while loading, nothing for
// a link the server couldn't read (preview: null), nothing on a network blip.
// A skeleton under every pasted link would out-noise the comments themselves.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import { Globe, Play } from 'lucide-react';
import { api, apiOrigin } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';

/** Shape of GET /link-preview's payload. Declared here, not in `shared`, on
 *  purpose: this shape has exactly one reader — the day a second one appears
 *  it can move (shared stays type-only either way). */
export interface LinkPreview {
  url: string;
  siteName: string | null;   // server sends null when the page names no og:site_name
  title: string;
  description: string | null; // ditto og:description — the card renders without it
  imageUrl: string | null;
  kind: 'video' | 'page';
}

/* ------------------------------------------------------------------ */
/* Link extraction (pure — safe to import and test under node)         */
/* ------------------------------------------------------------------ */

const BARE_URL_RE = /https?:\/\/[^\s<>"']+/g;

/** The handful of entities our sanitized HTML can carry inside a URL or its
 *  surrounding text. Not a general HTML decoder — just enough that
 *  href="…&amp;x=1" and pasted text round-trip. */
function decodeEntities(s: string): string {
  // &amp; LAST — so "&amp;lt;" decodes to "&lt;", not "<" (double-decode).
  return s
    .replace(/&quot;/gi, '"').replace(/&#0*39;/g, "'")
    .replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&');
}

/** Dedupe identity for a link. For Instagram/TikTok the PATH is the identity —
 *  the share sheet decorates every copy with tracking params (?igsh=…,
 *  ?utm_source=…), so the same reel pasted twice arrives as two different
 *  strings and used to render two identical cards. */
function linkKey(u: URL): string {
  const host = u.hostname.toLowerCase().replace(/^(www|m)\./, '');
  if (host === 'instagram.com' || host === 'tiktok.com') {
    // Keyed on the STRIPPED host — instagram.com and www.instagram.com are
    // the same reel, and that variant pair is exactly how dupes arrive.
    return `https://${host}${u.pathname.replace(/\/+$/, '')}`;
  }
  return u.toString();
}

/** Up to `max` unique external http(s) links from a rendered HTML body:
 *  `<a href>` targets first (what the editor produces), then bare URLs typed
 *  as plain text. Our own origins — the app and its API, i.e. task links and
 *  attachment URLs — are skipped: the app previewing itself is noise.
 *
 *  Deliberately string-based, no DOMParser. The DOM version had a real bug:
 *  textContent glues adjacent blocks with NO separator, so
 *  "<p>…instagram.com/p/X/</p><p>Super cinematic…</p>" scanned as
 *  "…/p/X/Super" — a mangled URL that dodged the dedupe and drew a second,
 *  identical card (Instagram soft-404s it back to the same reel). Replacing
 *  every tag with a space makes block boundaries unglueable — and makes this
 *  function honestly pure, testable under plain node. */
export function extractExternalLinks(html: string, max = 3): string[] {
  // Cheap bail — most comments carry no links at all.
  if (!html || !/https?:\/\//i.test(html)) return [];
  const own = new Set<string>();
  try { own.add(apiOrigin()); } catch { /* non-browser */ }
  if (typeof window !== 'undefined') own.add(window.location.origin);
  const found: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string) => {
    if (found.length >= max) return;
    let u: URL;
    try { u = new URL(raw); } catch { return; }
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return;
    if (own.has(u.origin)) return;
    const key = linkKey(u);
    if (seen.has(key)) return;
    seen.add(key);
    found.push(u.toString());
  };
  // Anchor targets first — the editor's own output, always clean.
  for (const m of html.matchAll(/\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')/gi)) {
    push(decodeEntities(m[1] ?? m[2] ?? ''));
  }
  // Bare URLs in the text (tags → SPACES, see above). Anchor labels are often
  // the URL itself — the dedupe absorbs those. Trailing sentence punctuation
  // ("check https://x.com/reel.") isn't part of a pasted URL; a lone closing
  // paren is only kept when the URL opened one (Wikipedia-style paths).
  const text = decodeEntities(html.replace(/<[^>]*>/g, ' '));
  for (const m of text.match(BARE_URL_RE) ?? []) {
    const cleaned = m.replace(/[.,;:!?]+$/, '');
    push(cleaned.endsWith(')') && !cleaned.includes('(') ? cleaned.slice(0, -1) : cleaned);
  }
  return found;
}

/** Video id when the URL is a YouTube watch / share / Shorts link, else null.
 *  Only YouTube gets in-app playback: its embeds are anonymous and stable.
 *  Instagram/TikTok stay card-only DELIBERATELY — their embeds are
 *  login-walled and revocable, and a card that always works beats an embed
 *  that sometimes doesn't. */
export function youTubeEmbedId(url: string): string | null {
  let u: URL;
  try { u = new URL(url); } catch { return null; }
  const host = u.hostname.toLowerCase().replace(/^(www|m)\./, '');
  const valid = (id: string | null | undefined) => (id && /^[A-Za-z0-9_-]{6,20}$/.test(id) ? id : null);
  if (host === 'youtu.be') return valid(u.pathname.split('/')[1]);
  if (host === 'youtube.com') {
    if (u.pathname === '/watch') return valid(u.searchParams.get('v'));
    if (u.pathname.startsWith('/shorts/')) return valid(u.pathname.split('/')[2]);
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Preview fetch — one request per URL per session                     */
/* ------------------------------------------------------------------ */

// Module-level: the same link in a description, three comments, and every
// re-render costs ONE request. The PROMISE is cached (not the value) so two
// cards racing for the same URL share the in-flight request too. Errors
// resolve to null — the same silence as "server had nothing usable"; a
// transient blip degrades to no card, never to a refetch storm.
const previewCache = new Map<string, Promise<LinkPreview | null>>();

function fetchPreview(url: string): Promise<LinkPreview | null> {
  let p = previewCache.get(url);
  if (!p) {
    p = api
      .get<{ preview: LinkPreview | null }>(`/link-preview?url=${encodeURIComponent(url)}`)
      .then((r) => r.preview)
      .catch(() => null);
    previewCache.set(url, p);
  }
  return p;
}

/* ------------------------------------------------------------------ */
/* Cards                                                               */
/* ------------------------------------------------------------------ */

function domainOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
}

function PreviewCard({ url, preview }: { url: string; preview: LinkPreview }) {
  const [playing, setPlaying] = useState(false);
  // A dead og:image degrades to the icon tile (the Avatar fallback pattern:
  // one broken remote image must never leave a broken-img glyph on screen).
  const [imgFailed, setImgFailed] = useState(false);
  const ytId = youTubeEmbedId(url);

  if (playing && ytId) {
    return (
      <iframe
        className="cg-linkcard-embed"
        src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1`}
        title={preview.title || 'YouTube video'}
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
      />
    );
  }

  const domain = domainOf(preview.url || url);
  const caption = preview.siteName && preview.siteName.toLowerCase() !== domain.toLowerCase()
    ? `${preview.siteName} · ${domain}`
    : domain;
  const showImage = Boolean(preview.imageUrl) && !imgFailed;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="cg-linkcard"
      onClick={ytId ? (e) => {
        // A plain click plays in place; modifier clicks keep their native
        // open-in-new-tab meaning (middle-click never fires onClick).
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        setPlaying(true);
      } : undefined}
    >
      <span className="cg-linkcard-thumb">
        {showImage ? (
          <img src={preview.imageUrl ?? ''} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setImgFailed(true)} />
        ) : (
          <Icon i={preview.kind === 'video' ? Play : Globe} size={20} />
        )}
        {showImage && preview.kind === 'video' && (
          <span className="cg-linkcard-play" aria-hidden>
            <span><Icon i={Play} size={14} className="fill-current" /></span>
          </span>
        )}
      </span>
      <span className="cg-linkcard-body">
        <span className="cg-linkcard-title">{preview.title || domain}</span>
        <span className="cg-linkcard-meta">{caption}</span>
      </span>
    </a>
  );
}

/** Preview cards for the external links inside a rendered task/comment HTML
 *  body. Renders NOTHING until at least one preview resolves — the cards then
 *  appear as a block below the content, so there is no reserved space and no
 *  layout shift while loading (and none ever, if nothing resolves). */
export function LinkPreviewCards({ html }: { html: string }) {
  const links = useMemo(() => extractExternalLinks(html), [html]);
  const [ready, setReady] = useState<Record<string, LinkPreview>>({});
  useEffect(() => {
    if (!links.length) return;
    let alive = true;
    setReady({});
    for (const u of links) {
      // Each card lands as ITS preview resolves — one slow host must not
      // hold back the others.
      void fetchPreview(u).then((p) => {
        if (alive && p) setReady((r) => (r[u] ? r : { ...r, [u]: p }));
      });
    }
    return () => { alive = false; };
  }, [links]);
  const shown = links.filter((u) => ready[u]);
  if (!shown.length) return null;
  return (
    <div className="cg-linkcards">
      {shown.map((u) => <PreviewCard key={u} url={u} preview={ready[u]} />)}
    </div>
  );
}
